(() => {
  'use strict';
  const API_TEMPLATE = '[data-api-related-template]';
  const PRODUCT_ITEM = '.product-item';
  const GRID_ITEM = '[data-grid-item]';
  const RELATED_SECTION = '[data-related-section]';
  const RECENTLY_VIEWED = 'recently-viewed';
  const GRID_SLIDER = 'grid-slider';
  const RECENT_WRAPPER = '[data-recently-viewed-wrapper]';
  const HIDDEN = 'hidden';
  const MOBILE_SLIDER = 'grid--mobile-slider';

  class RelatedProducts extends HTMLElement {
    constructor() {
      super();
      this.relatedItems = 0;
      this.wrapper = this.querySelector(RECENT_WRAPPER);
      this.recentlyViewed = this.querySelector(RECENTLY_VIEWED);
      this.relatedLoaded = false;
      this.observer = null;
    }

    connectedCallback() {
      this.loadRecentlyViewedProducts();

      if (!('IntersectionObserver' in window)) {
        this.loadRelatedProducts();
        return;
      }

      this.observer = new IntersectionObserver((entries, observer) => {
        if (!entries[0] || !entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRelatedProducts();
      }, { rootMargin: '0px 0px 600px 0px' });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    loadRelatedProducts() {
      if (this.relatedLoaded) return;
      this.relatedLoaded = true;

      const section = this.querySelector(RELATED_SECTION);
      if (!section) return;

      const productId = section.getAttribute('data-product-id');
      const limit = section.getAttribute('data-limit');
      const url = `${window.theme.routes.product_recommendations_url}?section_id=api-product-recommendation&limit=${limit}&product_id=${productId}&intent=related`;

      fetch(url)
        .then((response) => response.text())
        .then((html) => this.handleRelatedProductsResponse(html, section))
        .catch(() => this.hideSection(section));
    }

    handleRelatedProductsResponse(html, section) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const response = parsed.querySelector(API_TEMPLATE);

      if (!response) {
        this.hideSection(section);
        this.updateVisibility();
        return;
      }

      const container = document.createElement('div');
      container.innerHTML = response.innerHTML;
      const itemCount = container.querySelectorAll(GRID_ITEM).length;

      if (itemCount > 0) {
        section.innerHTML = container.innerHTML;
        this.relatedItems = itemCount;

        if (parseInt(section.style.getPropertyValue('--COLUMNS-MOBILE'), 10) === 0) {
          const firstGridItem = section.querySelector(GRID_ITEM);
          if (firstGridItem && firstGridItem.parentElement) firstGridItem.parentElement.classList.add(MOBILE_SLIDER);
        }
      } else {
        this.hideSection(section);
      }

      this.updateVisibility();
    }

    loadRecentlyViewedProducts() {
      if (!this.recentlyViewed) return;
      this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', () => this.handleRecentlyViewedResponse());
    }

    handleRecentlyViewedResponse() {
      const minimum = parseInt(this.recentlyViewed.dataset.minimum, 10) || 1;
      const items = this.recentlyViewed.querySelectorAll(PRODUCT_ITEM);
      const slider = this.recentlyViewed.querySelector(GRID_SLIDER);
      const canShowWithoutWrapper = !this.wrapper && items.length > 0;
      const canShowWithWrapper = this.wrapper && items.length >= minimum;

      if (canShowWithoutWrapper || canShowWithWrapper) {
        if (canShowWithWrapper) this.wrapper.classList.remove(HIDDEN);
        this.recentlyViewed.classList.remove(HIDDEN);
        this.recentlyViewed.dispatchEvent(new CustomEvent('theme:tab:check', { bubbles: true }));
        if (slider) slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
      }

      this.updateVisibility();
    }

    hideSection(section) {
      section.dispatchEvent(new CustomEvent('theme:tab:hide', { bubbles: true }));
    }

    updateVisibility() {
      const hide = Shopify.Products.getConfig().howManyToShow < 1 && this.relatedItems < 1;
      this.classList.toggle(HIDDEN, hide);
    }
  }

  if (!customElements.get('related-products')) customElements.define('related-products', RelatedProducts);
})();
